import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { supabase } from '../../config/supabase';
import { ownerService } from '../../services/ownerService';
import { checklistAutomationService, ChecklistAutomationRule } from '../../services/checklistAutomationService';
import { progressStageService } from '../../services/progressStageService';
import { progressStageSettingsService } from '../../services/progressStageSettingsService';
import { onboardingStepsService } from '../../services/onboardingStepsService';

interface ChecklistAutomationSettingsProps {
  onClose?: () => void;
}

interface AutomationRuleEdit {
  check_list_num: string;
  progress: number;
  isNew?: boolean;
}

const ChecklistAutomationSettings: React.FC<ChecklistAutomationSettingsProps> = ({ onClose }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [orgId, setOrgId] = useState<number | null>(null);
  const [rules, setRules] = useState<ChecklistAutomationRule[]>([]);
  const [editingRules, setEditingRules] = useState<Map<string, AutomationRuleEdit>>(new Map());
  const [newRule, setNewRule] = useState<AutomationRuleEdit | null>(null);
  const [progressStages, setProgressStages] = useState<any[]>([]);
  const [availableChecklistNums, setAvailableChecklistNums] = useState<string[]>([]);
  const [checklistOptions, setChecklistOptions] = useState<{ num: string; label: string }[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const currentOrgIdString = await ownerService.getOwnerOrgId();
      if (!currentOrgIdString) {
        setError('Unable to determine organization ID');
        return;
      }
      
      const currentOrgId = Number(currentOrgIdString);
      if (isNaN(currentOrgId)) {
        setError('Invalid organization ID');
        return;
      }
      
      setOrgId(currentOrgId);

      // Load automation rules (will return custom if exists, otherwise defaults)
      const loadedRules = await checklistAutomationService.getAutomationRules(currentOrgId);
      setRules(loadedRules);
      
      // Check if these are default rules (not saved to DB yet)
      const hasCustomRules = loadedRules.some(rule => !rule.id?.startsWith('default-'));
      if (!hasCustomRules && loadedRules.length > 0) {
        // Show a message that these are defaults
        console.log('Showing default automation rules (not yet saved to database)');
      }

      // Load progress stages from database (progress_stage_settings table)
      const stages = await progressStageSettingsService.getCurrentUserStages();
      setProgressStages(stages);

      // Load onboarding steps to get available checklist numbers
      const steps = await onboardingStepsService.getOnboardingSteps(currentOrgId);
      const checklistNums = steps.map((_, index) => (index + 1).toString());
      setAvailableChecklistNums(checklistNums);
      const options = steps.map((step, index) => ({
        num: (index + 1).toString(),
        label: step.title,
      }));
      setChecklistOptions(options);

    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load automation settings');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRule = (rule: ChecklistAutomationRule) => {
    const edit: AutomationRuleEdit = {
      check_list_num: rule.check_list_num,
      progress: rule.progress,
    };
    setEditingRules(new Map(editingRules.set(rule.id, edit)));
  };

  const handleCancelEdit = (ruleId: string) => {
    const newEditing = new Map(editingRules);
    newEditing.delete(ruleId);
    setEditingRules(newEditing);
  };

  const handleUpdateEdit = (ruleId: string, field: 'check_list_num' | 'progress', value: string | number) => {
    const edit = editingRules.get(ruleId);
    if (edit) {
      const updated = { ...edit, [field]: value };
      setEditingRules(new Map(editingRules.set(ruleId, updated)));
    }
  };

  const handleSaveEdit = async (ruleId: string) => {
    if (!orgId) return;

    const edit = editingRules.get(ruleId);
    if (!edit) return;

    try {
      setSaving(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated');
        return;
      }

      // Get checklist label from onboarding steps (may have changed if checklist number changed)
      const label = await checklistAutomationService.getChecklistLabel(orgId, edit.check_list_num);

      // Update the rule
      await checklistAutomationService.saveAutomationRule(
        orgId,
        edit.check_list_num,
        edit.progress,
        user.id,
        label || undefined
      );

      // Reload rules
      await loadData();
      handleCancelEdit(ruleId);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving rule:', err);
      setError(err.message || 'Failed to save automation rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (rule: ChecklistAutomationRule) => {
    if (!orgId) return;
    if (!window.confirm(`Are you sure you want to delete the rule for checklist ${rule.check_list_num}?`)) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await checklistAutomationService.deleteAutomationRule(orgId, rule.check_list_num);
      await loadData();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error deleting rule:', err);
      setError(err.message || 'Failed to delete automation rule');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNewRule = () => {
    setNewRule({ check_list_num: '', progress: 0, isNew: true });
  };

  const handleCancelNewRule = () => {
    setNewRule(null);
  };

  const handleSaveNewRule = async () => {
    if (!orgId || !newRule) return;

    if (!newRule.check_list_num) {
      setError('Please select a checklist number');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated');
        return;
      }

      // Get checklist label from onboarding steps
      const label = await checklistAutomationService.getChecklistLabel(orgId, newRule.check_list_num);

      await checklistAutomationService.saveAutomationRule(
        orgId,
        newRule.check_list_num,
        newRule.progress,
        user.id,
        label || undefined
      );

      await loadData();
      setNewRule(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving new rule:', err);
      setError(err.message || 'Failed to save automation rule');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    if (!orgId) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated');
        return;
      }

      // Save all current rules (including defaults) to database
      // This will create custom rules for the organization
      for (const rule of rules) {
        // Skip if it's already a custom rule (has real ID, not default-*)
        if (rule.id && !rule.id.startsWith('default-')) {
          continue;
        }
        
        // Use existing checklist_label if available, otherwise it will be fetched automatically
        await checklistAutomationService.saveAutomationRule(
          orgId,
          rule.check_list_num,
          rule.progress,
          user.id,
          rule.checklist_label
        );
      }

      // Reload to get the saved rules
      await loadData();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving automation rules:', err);
      setError(err.message || 'Failed to save automation rules');
    } finally {
      setSaving(false);
    }
  };

  const getProgressStageLabel = (progress: number): string => {
    const stage = progressStages.find(s => s.value === progress);
    return stage?.label || `Progress ${progress}`;
  };

  const getProgressStageColor = (progress: number): string => {
    const stage = progressStages.find(s => s.value === progress);
    return stage?.color || '#666666';
  };

  const getProgressStageGradient = (progress: number): string => {
    // Use gradient similar to header/buttons for better visual appeal
    if (theme.palette.mode === 'dark') {
      return 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)';
    } else {
      return 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)';
    }
  };

  const getProgressStageBorderColor = (progress: number): string => {
    if (theme.palette.mode === 'dark') {
      return 'rgba(102, 126, 234, 0.5)';
    } else {
      return 'rgba(102, 126, 234, 0.4)';
    }
  };

  const getUsedChecklistNums = (): Set<string> => {
    const used = new Set(rules.map(r => r.check_list_num));
    editingRules.forEach(edit => {
      if (edit.check_list_num) used.add(edit.check_list_num);
    });
    if (newRule?.check_list_num) {
      used.add(newRule.check_list_num);
    }
    return used;
  };

  const getAvailableChecklistNumsForSelect = (): string[] => {
    const used = getUsedChecklistNums();
    return availableChecklistNums.filter(num => !used.has(num));
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        mt: 3,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
          Checklist Automation Settings
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {orgId ? `Organization ID: ${orgId}` : ''}
        </Typography>
      </Box>

      <Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : error && !rules.length ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box>
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Automation settings saved successfully!
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Configure which progress stage {progressStages.length > 0 
                ? `(${Math.min(...progressStages.map(s => s.value))}-${Math.max(...progressStages.map(s => s.value))})` 
                : '(0-6)'} a hire should be automatically assigned to based on their checklist number.
              When a hire completes a checklist step, their progress will be updated according to these rules.
            </Typography>
            
            {rules.length > 0 && rules[0]?.id?.startsWith('default-') && (
              <Alert severity="info" sx={{ mb: 2 }}>
                You are currently viewing default automation rules. Click "Save Changes" to save these as custom rules for your organization.
              </Alert>
            )}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Checklist</TableCell>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell sx={{ fontWeight: 600 }}>Progress Stage</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.map((rule) => {
                    const isEditing = editingRules.has(rule.id);
                    const edit = editingRules.get(rule.id);

                    return (
                      <TableRow key={rule.id}>
                        <TableCell>
                          {isEditing ? (
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                              <InputLabel>Checklist</InputLabel>
                              <Select
                                value={edit?.check_list_num || ''}
                                onChange={(e) => handleUpdateEdit(rule.id, 'check_list_num', e.target.value as string)}
                                label="Checklist"
                              >
                                {checklistOptions.map((opt) => (
                                  <MenuItem key={opt.num} value={opt.num}>
                                    {`${opt.num}: ${opt.label}`}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {rule.check_list_num}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: rule.checklist_label ? 'normal' : 'italic', fontSize: '0.875rem' }}>
                                {rule.checklist_label || 'Loading...'}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <ArrowForwardIcon
                            sx={{
                              color: theme.palette.mode === 'dark' ? '#90caf9' : '#667eea',
                              opacity: 0.9,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                              <InputLabel>Progress Stage</InputLabel>
                              <Select
                                value={edit?.progress ?? 0}
                                onChange={(e) => handleUpdateEdit(rule.id, 'progress', e.target.value as number)}
                                label="Progress Stage"
                              >
                                {progressStages.map((stage) => (
                                  <MenuItem key={stage.value} value={stage.value}>
                                    {stage.value}: {stage.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Chip
                              label={`${rule.progress}: ${getProgressStageLabel(rule.progress)}`}
                              sx={{
                                background: getProgressStageGradient(rule.progress),
                                color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                                fontWeight: 600,
                                border: `1.5px solid ${getProgressStageBorderColor(rule.progress)}`,
                                boxShadow: theme.palette.mode === 'dark'
                                  ? '0 2px 8px rgba(102, 126, 234, 0.2)'
                                  : '0 2px 8px rgba(102, 126, 234, 0.15)',
                                '&:hover': {
                                  background: theme.palette.mode === 'dark'
                                    ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.4) 0%, rgba(118, 75, 162, 0.4) 100%)'
                                    : 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
                                  boxShadow: theme.palette.mode === 'dark'
                                    ? '0 4px 12px rgba(102, 126, 234, 0.3)'
                                    : '0 4px 12px rgba(102, 126, 234, 0.25)',
                                },
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleSaveEdit(rule.id)}
                                disabled={saving}
                              >
                                Save
                              </Button>
                              <Button
                                size="small"
                                onClick={() => handleCancelEdit(rule.id)}
                                disabled={saving}
                              >
                                Cancel
                              </Button>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Edit Rule">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditRule(rule)}
                                  disabled={saving}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Rule">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteRule(rule)}
                                  disabled={saving}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  
                  {newRule && (
                    <TableRow>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                          <InputLabel>Checklist Number</InputLabel>
                          <Select
                            value={newRule.check_list_num}
                            onChange={(e) => setNewRule({ ...newRule, check_list_num: e.target.value })}
                            label="Checklist Number"
                          >
                            {checklistOptions
                              .filter(opt => getAvailableChecklistNumsForSelect().includes(opt.num))
                              .map((opt) => (
                                <MenuItem key={opt.num} value={opt.num}>
                                  {`${opt.num}: ${opt.label}`}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="center">
                        <ArrowForwardIcon
                          sx={{
                            color: theme.palette.mode === 'dark' ? '#90caf9' : '#667eea',
                            opacity: 0.9,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel>Progress Stage</InputLabel>
                          <Select
                            value={newRule.progress}
                            onChange={(e) => setNewRule({ ...newRule, progress: e.target.value as number })}
                            label="Progress Stage"
                          >
                            {progressStages.map((stage) => (
                              <MenuItem key={stage.value} value={stage.value}>
                                {stage.value}: {stage.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${newRule.progress}: ${getProgressStageLabel(newRule.progress)}`}
                          sx={{
                            background: getProgressStageGradient(newRule.progress),
                            color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                            fontWeight: 600,
                            border: `1.5px solid ${getProgressStageBorderColor(newRule.progress)}`,
                            boxShadow: theme.palette.mode === 'dark'
                              ? '0 2px 8px rgba(102, 126, 234, 0.2)'
                              : '0 2px 8px rgba(102, 126, 234, 0.15)',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleSaveNewRule}
                            disabled={saving || !newRule.check_list_num}
                          >
                            Save
                          </Button>
                          <Button
                            size="small"
                            onClick={handleCancelNewRule}
                            disabled={saving}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}

                  {rules.length === 0 && !newRule && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="textSecondary">
                          No automation rules configured. Default rules will be used automatically. Click "Add Rule" to create a custom rule.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {!newRule && (
              <Box sx={{ mt: 2 }}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddNewRule}
                  variant="outlined"
                  disabled={saving}
                >
                  Add Rule
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        {onClose && (
          <Button
            onClick={onClose}
            startIcon={<CancelIcon />}
            disabled={saving}
          >
            Close
          </Button>
        )}
        <Button
          onClick={handleSaveAll}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving || loading}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Paper>
  );
};

export default ChecklistAutomationSettings;
